/**
 * POSCatalog — pane з каталогом номенклатур + пошуком + сканером.
 *
 * Завантажує номенклатури через `nomenclature.list`, фільтрує локально.
 * Кнопка камери відкриває BarcodeScanner. Lookup за штрих-кодом викликає
 * GET /nomenclature/lookup?barcode=X.
 *
 * Mobile: компонент рендериться як bottom-sheet (керується класом `--open`).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { nomenclature, type Nomenclature } from '@/api/catalogs';
import {
  retailNomenclaturePrices,
  type NomenclatureRetailPrice,
} from '@/api/retailNomenclaturePrices';
import BarcodeScanner from './BarcodeScanner';

interface Props {
  organizationId: string;
  priceGroupId: string | null;
  onAddItem: (n: Nomenclature, price: number) => void;
  /** Для mobile: чи open drawer. На desktop ігнорується (завжди видно). */
  isOpen?: boolean;
  onClose?: () => void;
}

export default function POSCatalog({
  organizationId,
  priceGroupId,
  onAddItem,
  isOpen = true,
  onClose,
}: Props) {
  const [query, setQuery] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const nomQ = useQuery({
    queryKey: ['pos-nomenclature', query],
    queryFn: () => nomenclature.list({ q: query || undefined, limit: 100 }),
    staleTime: 60 * 1000,
  });

  const pricesQ = useQuery({
    queryKey: ['pos-prices', organizationId, priceGroupId],
    queryFn: () =>
      retailNomenclaturePrices.list({
        organization_id: organizationId,
        price_group_id: priceGroupId ?? undefined,
        is_active: true,
        limit: 500,
      }),
    enabled: !!priceGroupId,
    staleTime: 60 * 1000,
  });

  // Мапа: nomenclature_id → price (для default_price_group)
  const priceMap = useMemo(() => {
    const m = new Map<string, number>();
    pricesQ.data?.items.forEach((p: NomenclatureRetailPrice) => {
      m.set(p.nomenclature_id, Number(p.price));
    });
    return m;
  }, [pricesQ.data]);

  const handleScan = async (barcode: string) => {
    setScannerOpen(false);
    setScanError(null);
    try {
      const nom = await nomenclature.lookupByBarcode(barcode);
      const price = priceMap.get(nom.id) ?? 0;
      onAddItem(nom, price);
    } catch {
      setScanError(`Товар з штрих-кодом ${barcode} не знайдено`);
    }
  };

  const fmt = (v: number) =>
    v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={`pos-catalog${isOpen ? ' pos-catalog--open' : ''}`}>
      {onClose && (
        <div className="pos-catalog__drawer-handle" onClick={onClose}>
          ▼ Згорнути каталог
        </div>
      )}
      <div className="pos-catalog__controls">
        <input
          type="text"
          className="pos-catalog__search"
          placeholder="Пошук товару..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <button
          className="pos-catalog__scan-btn"
          onClick={() => setScannerOpen(true)}
          title="Сканувати штрих-код"
        >
          📷
        </button>
      </div>

      {scanError && (
        <div className="pos-card-info" style={{ color: 'var(--danger)' }}>
          {scanError}
        </div>
      )}

      <div className="pos-catalog__list">
        {nomQ.data?.items.map((n) => {
          const price = priceMap.get(n.id) ?? 0;
          return (
            <button
              key={n.id}
              className="pos-catalog__tile"
              onClick={() => onAddItem(n, price)}
              disabled={!priceGroupId}
              title={!priceGroupId ? 'Workstation без price_group' : undefined}
            >
              <div className="pos-catalog__tile-name">{n.name}</div>
              <div className="pos-catalog__tile-price">
                {price > 0 ? `${fmt(price)} грн` : '— ціна —'}
              </div>
            </button>
          );
        })}
      </div>

      {scannerOpen && (
        <BarcodeScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />
      )}
    </div>
  );
}
